import React from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, ImageIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { logger } from "@/lib/logger";

export default function Characters() {
  const { projectId } = useParams();
  const [chars, setChars] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [portraitBusy, setPortraitBusy] = React.useState({});
  const { updateCredits } = useAuth();

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/characters`);
      setChars(data);
    } catch (err) {
      logger.warn("characters: load failed", err);
      toast.error("Could not load characters");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => { load(); }, [load]);

  const openEdit = (c) => setEditing({ ...c });

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/projects/${projectId}/characters/${editing.id}`, editing);
      toast.success("Character updated");
      setEditing(null);
      load();
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const generateProfile = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/character-profile", { project_id: projectId, character_id: editing.id });
      setEditing({ ...editing, ...data.profile });
      updateCredits(data.credits);
      toast.success("Profile drafted — review and save.");
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };

  const generatePortrait = async (c) => {
    setPortraitBusy((s) => ({ ...s, [c.id]: true }));
    try {
      const { data } = await api.post("/storyboards/portrait", { project_id: projectId, kind: "character", entity_id: c.id });
      updateCredits(data.credits);
      toast.success("Portrait generated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setPortraitBusy((s) => ({ ...s, [c.id]: false })); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto px-8 py-10">
        <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Character Lab</div>
        <h1 className="font-editorial text-4xl mb-8">Cast of characters</h1>

        {loading ? (
          <div className="text-[#A1A1AA]">Loading…</div>
        ) : chars.length === 0 ? (
          <div className="border border-[#1E1E1E] bg-[#0F0F10] p-12 text-center">
            <div className="font-editorial text-2xl">No characters yet.</div>
            <p className="text-sm text-[#A1A1AA] mt-2">
              Your screenplay will build this board automatically as you write character cues.
            </p>
            <Link to={`/app/projects/${projectId}/script`} className="mt-6 inline-block bg-[#D4AF37] text-[#0A0A0A] px-5 py-2 text-sm font-medium">
              Open the screenplay
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {chars.map((c) => (
              <div key={c.id} className="border border-[#1E1E1E] bg-[#141414] p-5 hover:border-[#27272A] transition-colors" data-testid={`char-card-${c.id}`}>
                <PortraitBox character={c} projectId={projectId} onGenerate={() => generatePortrait(c)} busy={portraitBusy[c.id]} />
                <div className="mt-4 flex items-baseline justify-between">
                  <div className="font-editorial text-2xl">{c.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{c.role}</div>
                </div>
                <div className="mt-2 text-xs text-[#71717A]">
                  {c.scene_count} scenes · {c.line_count} lines
                </div>
                <div className="mt-3 text-sm text-[#A1A1AA] leading-relaxed min-h-[3rem] line-clamp-3">
                  {c.description || <span className="italic text-[#71717A]">No description. Add one to unlock deeper analysis.</span>}
                </div>
                <button onClick={() => openEdit(c)}
                  className="mt-4 w-full border border-[#27272A] hover:border-[#D4AF37] text-xs uppercase tracking-widest py-2 transition-colors"
                  data-testid={`char-edit-${c.id}`}>
                  Edit profile
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-[#0A0A0A]/85 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#141414] border border-[#27272A] p-8" onClick={(e) => e.stopPropagation()} data-testid="char-edit-modal">
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <div className="uppercase text-xs tracking-widest text-[#D4AF37] mb-2">Character</div>
                <h2 className="font-editorial text-3xl">{editing.name}</h2>
              </div>
              <button onClick={generateProfile} disabled={busy}
                className="border border-[#D4AF37] text-[#D4AF37] px-3 py-1.5 text-xs hover:bg-[#D4AF37] hover:text-[#0A0A0A] inline-flex items-center gap-2 transition-colors"
                data-testid="char-ai-profile">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI draft
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <Field label="Age" value={editing.age} onChange={(v) => setEditing({ ...editing, age: v })} />
              <SelectField label="Role" value={editing.role} onChange={(v) => setEditing({ ...editing, role: v })}
                options={["protagonist", "antagonist", "supporting", "minor"]} />
            </div>
            <div className="space-y-4">
              <TextArea label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} />
              <TextArea label="Goal (external want)" value={editing.goal} onChange={(v) => setEditing({ ...editing, goal: v })} />
              <TextArea label="Motivation (internal need)" value={editing.motivation} onChange={(v) => setEditing({ ...editing, motivation: v })} />
              <div className="grid grid-cols-2 gap-4">
                <TextArea label="Fear" value={editing.fear} onChange={(v) => setEditing({ ...editing, fear: v })} rows={2} />
                <TextArea label="Flaw" value={editing.flaw} onChange={(v) => setEditing({ ...editing, flaw: v })} rows={2} />
              </div>
              <TextArea label="Arc" value={editing.arc} onChange={(v) => setEditing({ ...editing, arc: v })} />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="text-sm text-[#A1A1AA] px-4 py-2">Cancel</button>
              <button onClick={save} disabled={busy}
                className="bg-[#D4AF37] text-[#0A0A0A] px-6 py-2.5 text-sm font-medium disabled:opacity-50" data-testid="char-save-btn">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function PortraitBox({ character, projectId, onGenerate, busy }) {
  const [dataUrl, setDataUrl] = React.useState(null);
  React.useEffect(() => {
    if (character.portrait_asset_id) {
      api.get(`/storyboards/asset/${character.portrait_asset_id}`).then((r) => setDataUrl(r.data.data_url)).catch(() => {});
    } else {
      setDataUrl(null);
    }
  }, [character.portrait_asset_id]);

  return (
    <div className="aspect-[3/4] bg-[#0A0A0A] border border-[#27272A] relative overflow-hidden">
      {dataUrl ? (
        <img src={dataUrl} alt={character.name} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-[#71717A] gap-2">
          <ImageIcon size={22} strokeWidth={1.5} />
          <div className="text-[10px] uppercase tracking-widest">No portrait</div>
        </div>
      )}
      <button onClick={onGenerate} disabled={busy}
        className="absolute bottom-2 right-2 bg-[#0A0A0A]/90 border border-[#27272A] hover:border-[#D4AF37] text-[10px] uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1 transition-colors"
        data-testid={`char-portrait-gen-${character.id}`}>
        {busy ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} className="text-[#D4AF37]" />}
        {dataUrl ? "Regenerate" : "Generate"}
      </button>
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-[#71717A] mb-1.5">{label}</div>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#D4AF37] focus:outline-none text-[#FFFFF0] px-3 py-2 text-sm" />
    </label>
  );
}
function TextArea({ label, value, onChange, rows = 3 }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-[#71717A] mb-1.5">{label}</div>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows}
        className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#D4AF37] focus:outline-none text-[#FFFFF0] px-3 py-2 text-sm leading-relaxed" />
    </label>
  );
}
function SelectField({ label, value, onChange, options }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-[#71717A] mb-1.5">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#D4AF37] focus:outline-none text-[#FFFFF0] px-3 py-2 text-sm">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
